/**
 * Centralised TanStack Query keys, so invalidation never depends on a string typed twice.
 * Each feature has its own namespace; invalidating `<namespace>.all` refreshes all of it.
 */
type Filter = Readonly<Record<string, unknown>>;

export const QUERY_KEYS = {
  auth: {
    all: ['auth'] as const,
    me: () => [...QUERY_KEYS.auth.all, 'me'] as const,
  },
  members: {
    all: ['members'] as const,
    list: (filter: Filter) => [...QUERY_KEYS.members.all, 'list', filter] as const,
    /** The members list loaded as the user scrolls. */
    feed: (filter: Filter) => [...QUERY_KEYS.members.all, 'feed', filter] as const,
    detail: (memberId: number) => [...QUERY_KEYS.members.all, 'detail', memberId] as const,
    account: (memberId: number) => [...QUERY_KEYS.members.all, 'account', memberId] as const,
    /** `photoPath` is part of the key, so a replaced photo is fetched instead of served stale. */
    photo: (memberId: number, photoPath: string) =>
      [...QUERY_KEYS.members.all, 'photo', memberId, photoPath] as const,
  },
  memberships: {
    all: ['memberships'] as const,
    byMember: (memberId: number, filter: Filter) =>
      [...QUERY_KEYS.memberships.all, 'member', memberId, filter] as const,
    active: (memberId: number) =>
      [...QUERY_KEYS.memberships.all, 'member', memberId, 'active'] as const,
    expiring: (filter: Filter) => [...QUERY_KEYS.memberships.all, 'expiring', filter] as const,
    /** The renewal list loaded as the user scrolls. */
    expiringFeed: (filter: Filter) =>
      [...QUERY_KEYS.memberships.all, 'expiring-feed', filter] as const,
  },
  invoices: {
    all: ['invoices'] as const,
    list: (filter: Filter) => [...QUERY_KEYS.invoices.all, 'list', filter] as const,
    detail: (id: number) => [...QUERY_KEYS.invoices.all, 'detail', id] as const,
    summary: (range?: Filter) => [...QUERY_KEYS.invoices.all, 'summary', range ?? {}] as const,
    collections: (range?: Filter) =>
      [...QUERY_KEYS.invoices.all, 'collections', range ?? {}] as const,
    byMember: (memberId: number, filter: Filter) =>
      [...QUERY_KEYS.invoices.all, 'member', memberId, filter] as const,
    /** Bill lists loaded as the user scrolls. */
    feed: (filter: Filter) => [...QUERY_KEYS.invoices.all, 'feed', filter] as const,
  },
  attendance: {
    all: ['attendance'] as const,
    punches: (filter: Filter) => [...QUERY_KEYS.attendance.all, 'punches', filter] as const,
    checkIns: (date: string) => [...QUERY_KEYS.attendance.all, 'check-ins', date] as const,
    devices: () => [...QUERY_KEYS.attendance.all, 'devices'] as const,
    summary: (date: string) => [...QUERY_KEYS.attendance.all, 'summary', date] as const,
    inactive: (days: number) => [...QUERY_KEYS.attendance.all, 'inactive', days] as const,
    peakHours: (from: string, to: string) =>
      [...QUERY_KEYS.attendance.all, 'peak-hours', from, to] as const,
    users: (filter: Filter) => [...QUERY_KEYS.attendance.all, 'users', filter] as const,
    member: (memberId: number) => [...QUERY_KEYS.attendance.all, 'member', memberId] as const,
    command: (id: number) => [...QUERY_KEYS.attendance.all, 'command', id] as const,
  },
  ptSales: {
    all: ['pt-sales'] as const,
    byMember: (memberId: number, filter: Filter) =>
      [...QUERY_KEYS.ptSales.all, 'member', memberId, filter] as const,
  },
  prospects: {
    all: ['prospects'] as const,
    list: (filter: Filter) => [...QUERY_KEYS.prospects.all, 'list', filter] as const,
    /** Enquiry lists loaded as the user scrolls. */
    feed: (filter: Filter) => [...QUERY_KEYS.prospects.all, 'feed', filter] as const,
  },
  packages: {
    all: ['packages'] as const,
    membership: (filter: Filter) => [...QUERY_KEYS.packages.all, 'membership', filter] as const,
    pt: (filter: Filter) => [...QUERY_KEYS.packages.all, 'pt', filter] as const,
  },
  staff: {
    all: ['staff'] as const,
    list: (filter: Filter) => [...QUERY_KEYS.staff.all, 'list', filter] as const,
    /** Names for the "owner" pickers on the member and enquiry forms. */
    options: () => [...QUERY_KEYS.staff.all, 'options'] as const,
  },
  roles: {
    all: ['roles'] as const,
    list: () => [...QUERY_KEYS.roles.all, 'list'] as const,
    permissions: () => [...QUERY_KEYS.roles.all, 'permissions'] as const,
  },
} as const;
